import { Activity, RoleType } from "common";
import { FloatingLayer } from "../utils/FloatingLayer";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import { get12HrTime } from "../utils/time";
import { useIsInRole } from "../utils/useIsInRole";
import { MdDelete, MdEdit } from "react-icons/md";
import { useCallback, useRef, useState } from "react";
import { useAPI } from "../api/useAPI";
import { FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { MarkdownRenderer } from "../utils/MarkdownRenderer";

interface SelectedActivityFloatingLayerProps {
  selectedActivity?: Activity;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (activityId: number) => void;
  onDeleteSuccess?: () => void;
}

export const SelectedActivityFloatingLayer = ({
  selectedActivity,
  isOpen,
  onClose,
  onEdit,
  onDeleteSuccess,
}: SelectedActivityFloatingLayerProps) => {
  const api = useAPI();
  const {
    isOpen: alertIsOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const isAdmin = useIsInRole(RoleType.Admin);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const onDelete = useCallback(async () => {
    if (!selectedActivity) {
      return;
    }
    setIsLoading(true);
    await api.activityDelete({ id: selectedActivity.id });
    onAlertClose();
    onClose();
    setIsLoading(false);
    onDeleteSuccess?.();
  }, [api, onAlertClose, onClose, onDeleteSuccess, selectedActivity]);

  return (
    <>
      {selectedActivity ? (
        <AlertDialog
          isOpen={alertIsOpen}
          leastDestructiveRef={cancelRef}
          onClose={onAlertClose}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Activity "{selectedActivity.title}"
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure? You can't undo this action afterwards.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button
                  ref={cancelRef}
                  onClick={onAlertClose}
                  isLoading={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={onDelete}
                  ml={3}
                  isLoading={isLoading}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      ) : null}
      <FloatingLayer padding={"2rem"} isOpen={isOpen} onClose={onClose}>
        {selectedActivity ? (
          <Box>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Box fontWeight={"bold"} fontSize={"1.5rem"}>
                {selectedActivity.title}
              </Box>
              <Box display={"flex"} alignItems={"center"}>
                <IconButton
                  size={"lg"}
                  variant={"ghost"}
                  aria-label="View"
                  icon={<FaEye />}
                  onClick={() => navigate(`/activity/${selectedActivity.id}`)}
                />
                {isAdmin ? (
                  <>
                    <IconButton
                      size={"lg"}
                      variant={"ghost"}
                      aria-label="Edit"
                      icon={<MdEdit />}
                      onClick={() => onEdit?.(selectedActivity.id)}
                    />
                    <IconButton
                      colorScheme="red"
                      size={"lg"}
                      variant={"ghost"}
                      aria-label="Edit"
                      icon={<MdDelete />}
                      onClick={onAlertOpen}
                    />
                  </>
                ) : null}
              </Box>
            </Box>
            <Box
              opacity={"0.6"}
              fontSize={"1.2rem"}
              fontWeight={"bold"}
            >{`${get12HrTime(selectedActivity.startTime)} - ${get12HrTime(
              selectedActivity.endTime
            )}`}</Box>
            <Divider marginTop={"1rem"} marginBottom={"1rem"} />
            <MarkdownRenderer>{selectedActivity.description}</MarkdownRenderer>
          </Box>
        ) : null}
      </FloatingLayer>
    </>
  );
};
